import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

// Visually-hidden aria-live region mounted once at the App root. Pages
// push short strings via `useAnnouncer().announce(text)` to surface
// state changes (turn started, oven finished, plant ripe) to screen
// readers without a visible toast. Polite by default — no interruption.
//
// Each push toggles the rendered text between two slots so identical
// successive messages still re-announce (assistive tech otherwise
// dedupes when textContent doesn't change).

type Announce = (message: string, opts?: { assertive?: boolean }) => void;

const Ctx = createContext<Announce>(() => {});

export function LiveAnnouncerProvider({ children }: { children: ReactNode }) {
  const [politeA, setPoliteA] = useState("");
  const [politeB, setPoliteB] = useState("");
  const [assertA, setAssertA] = useState("");
  const [assertB, setAssertB] = useState("");
  const toggleRef = useRef(false);
  // Coalesce a burst of identical messages within one animation frame —
  // foraging in particular can ripen multiple plants on a single tick.
  const lastRef = useRef<{ msg: string; at: number } | null>(null);

  const announce: Announce = useCallback((message, opts) => {
    const msg = (message ?? "").trim();
    if (!msg) return;
    const now = Date.now();
    const last = lastRef.current;
    if (last && last.msg === msg && now - last.at < 1000) return;
    lastRef.current = { msg, at: now };
    const flip = toggleRef.current;
    toggleRef.current = !flip;
    if (opts?.assertive) {
      if (flip) setAssertA(msg);
      else setAssertB(msg);
    } else {
      if (flip) setPoliteA(msg);
      else setPoliteB(msg);
    }
  }, []);

  return (
    <Ctx.Provider value={announce}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="doi-sr-only"
      >
        {politeA}
      </div>
      <div
        aria-live="polite"
        aria-atomic="true"
        className="doi-sr-only"
      >
        {politeB}
      </div>
      <div
        aria-live="assertive"
        aria-atomic="true"
        role="alert"
        className="doi-sr-only"
      >
        {assertA}
      </div>
      <div
        aria-live="assertive"
        aria-atomic="true"
        role="alert"
        className="doi-sr-only"
      >
        {assertB}
      </div>
    </Ctx.Provider>
  );
}

export function useAnnouncer(): Announce {
  return useContext(Ctx);
}

// Throttle wrapper for chatty event sources (per-tick foraging
// announcements). Returns a function that announces at most once per
// `windowMs`, with the most recent message dropping any in-flight one.
export function useThrottledAnnouncer(windowMs = 1500): Announce {
  const announce = useAnnouncer();
  const lastAtRef = useRef(0);
  const pendingRef = useRef<{ msg: string; assertive?: boolean } | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  return useCallback(
    (msg, opts) => {
      const now = Date.now();
      const elapsed = now - lastAtRef.current;
      if (elapsed >= windowMs) {
        lastAtRef.current = now;
        announce(msg, opts);
        return;
      }
      pendingRef.current = { msg, assertive: opts?.assertive };
      if (timerRef.current != null) return;
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        const p = pendingRef.current;
        pendingRef.current = null;
        if (p) {
          lastAtRef.current = Date.now();
          announce(p.msg, { assertive: p.assertive });
        }
      }, windowMs - elapsed);
    },
    [announce, windowMs],
  );
}
